var CryptoJS = require("crypto-js");

const storagePrefix = process.env.REACT_APP_STORAGE_PREFIX;

// Safety-net expiry for the warehouse list cache: bounds staleness even if some future
// write path forgets to call clearWarehouseListCache(), and — since old cache entries were
// stored as a bare array with no `cachedAt` field — doubles as a one-time migration that
// discards any pre-existing stale cache from before this expiry was introduced.
const WAREHOUSE_LIST_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes


/**
 * Storage management in app via localStorage
 *
 * @type {{ getToken: () => any; setToken: (token: string) => void; getRefreshToken: () => any; setRefreshToken: (token: string) => void; clearToken: () => void; setData: (key: string, data: any) => void; getData: (key: string) => any; }}
 */
const storage = {
  getToken: () => {
    return JSON.parse(window.localStorage.getItem(`${storagePrefix}token`) as string);
  },
  setToken: (token: string) => {
    window.localStorage.setItem(`${storagePrefix}token`, JSON.stringify(token));
  },
  getRefreshToken: () => {
    return JSON.parse(window.localStorage.getItem(`${storagePrefix}refreshToken`) as string);
  },
  setRefreshToken: (token: string) => {
    window.localStorage.setItem(`${storagePrefix}refreshToken`, JSON.stringify(token));
  },
  clearToken: () => {
    window.localStorage.removeItem(`${storagePrefix}token`);
    window.localStorage.removeItem(`${storagePrefix}refreshToken`);
    window.localStorage.removeItem(`timestamp`);
    storage.clearWarehouseListCache();
    window.localStorage.removeItem(`${storagePrefix}carrierList`);
  },
  setData: (key: string, data: any) => {
    const cipherText = CryptoJS.AES.encrypt(JSON.stringify(data), storagePrefix);
    window.localStorage.setItem(key, cipherText);
  },
  getData: (key: string) => {
    const dataString = window.localStorage.getItem(key) ? window.localStorage.getItem(key) : '';
    if (dataString) {
      const bytes = CryptoJS.AES.decrypt(dataString, storagePrefix);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    }
  },
  clearData: (key: string) => {
    window.localStorage.removeItem(key);
  },
  removePermissionAndLabel: () => {
    window.localStorage.removeItem('role_id');
    window.localStorage.removeItem('permissions');
    window.localStorage.removeItem('label');
    window.localStorage.removeItem('reviewData');
  },
  setWarehouseList: (type: string, data: any) => {
    const key = `${storagePrefix}warehouseList${type ? '_' + type : ''}`;
    window.localStorage.setItem(key, JSON.stringify({ data, cachedAt: Date.now() }));
  },
  getWarehouseList: (type: string = '') => {
    const key = `${storagePrefix}warehouseList${type ? '_' + type : ''}`;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;

    let parsed: any;
    try {
      parsed = JSON.parse(raw);
    } catch {
      window.localStorage.removeItem(key);
      return null;
    }

    // Pre-existing entries from before the TTL/versioning change are a bare array
    // (no `cachedAt`) — treat them as expired so they get refetched instead of served forever.
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) || !('cachedAt' in parsed)) {
      window.localStorage.removeItem(key);
      return null;
    }

    if (Date.now() - parsed.cachedAt > WAREHOUSE_LIST_CACHE_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }

    return parsed.data;
  },
  // Warehouse list is cached per-`type` (e.g. warehouseList, warehouseList_ORDER,
  // warehouseList_INVENTORY). Clearing by key prefix — instead of listing each known
  // type — guarantees every bucket is invalidated on a warehouse write, including any
  // new `type` a future consumer introduces, without needing to keep a type list in sync.
  clearWarehouseListCache: () => {
    const prefix = `${storagePrefix}warehouseList`;
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => window.localStorage.removeItem(key));
  },
  setCarrierList: (data: any) => {
    window.localStorage.setItem(`${storagePrefix}carrierList`, JSON.stringify(data));
  },
  getCarrierList: () => {
    const data = window.localStorage.getItem(`${storagePrefix}carrierList`);
    return data ? JSON.parse(data) : null;
  },
};

export default storage;
